import uniq from 'lodash/uniq'
import type { EventMatch } from './types'

type UserId = string

/**
 * Rank players from rotating doubles matches.
 *
 * This is a separate function from getRanksForMatches because rotating doubles
 * differs structurally in two ways: each side has multiple players (not one), and
 * head-to-head is an unreliable tiebreaker — any two players meet as opponents across
 * multiple rounds with different partners, so their h2h record reflects partnership
 * luck as much as individual skill. Seeding is used as the final tiebreaker instead.
 *
 * Primary:      Total sets won (sets where player's side scored more than opponent).
 * Tiebreaker 1: Total games won across all sets (raw game count, win or lose).
 * Tiebreaker 2: Seeding position (lower index in playerIds = higher seed = ranks first).
 *
 * Example — all sets end 4-0, stronger seeding always wins:
 *   R1: P1 sits out, P2+P3 vs P4+P5 → P2+P3 win
 *   R2: P2 sits out, P1+P4 vs P3+P5 → P1+P4 win
 *   R3: P3 sits out, P1+P5 vs P2+P4 → P1+P5 win
 *   R4: P4 sits out, P1+P3 vs P2+P5 → P1+P3 win
 *   R5: P5 sits out, P1+P2 vs P3+P4 → P1+P2 win
 *
 *   Sets won:     P1=4,  P2=2, P3=2, P4=1, P5=1  (primary decides P1 first)
 *   Games won:    P1=16, P2=8, P3=8, P4=4, P5=4  (tiebreaker 1 doesn't help here
 *                                                   since games=4×sets for all players)
 *   Final ranking: P1 > P2 > P3 > P4 > P5  (P2 vs P3 and P4 vs P5 resolved by seeding)
 *
 * Note: with uniform 4-0 scores, games won = 4 × sets won for every player, so
 * tiebreaker 1 never adds information beyond the primary. Seeding always decides ties.
 */
export function getRanksForRotatingDoubles(
  matches: EventMatch[],
  playerIds: UserId[],
): UserId[] {
  const scoreMap = playerIds.reduce<
    Record<UserId, { gamesWon: number; setsWon: number }>
  >((acc, userId) => {
    acc[userId] = { gamesWon: 0, setsWon: 0 }
    return acc
  }, {})

  for (const match of matches) {
    // biome-ignore lint/style/noNonNullAssertion: player id is always set
    const side1Players = match.sides[0].players.map((p) => p.id!)
    // biome-ignore lint/style/noNonNullAssertion: player id is always set
    const side2Players = match.sides[1].players.map((p) => p.id!)

    for (const set of match.sets) {
      const side1WonSet = set.side1Score > set.side2Score
      for (const uid of side1Players) {
        if (uid in scoreMap) {
          scoreMap[uid].gamesWon += set.side1Score
          if (side1WonSet) scoreMap[uid].setsWon += 1
        }
      }
      for (const uid of side2Players) {
        if (uid in scoreMap) {
          scoreMap[uid].gamesWon += set.side2Score
          if (!side1WonSet) scoreMap[uid].setsWon += 1
        }
      }
    }
  }

  return [...playerIds].sort((a, b) => {
    const scoreA = scoreMap[a]
    const scoreB = scoreMap[b]

    if (scoreA.setsWon !== scoreB.setsWon) {
      return scoreB.setsWon - scoreA.setsWon
    }

    if (scoreA.gamesWon !== scoreB.gamesWon) {
      return scoreB.gamesWon - scoreA.gamesWon
    }

    // Fall back to seeding position — deterministic and fair given h2h is inconclusive.
    return playerIds.indexOf(a) - playerIds.indexOf(b)
  })
}

export function rankTiedGroup(
  group: UserId[],
  matches: EventMatch[],
  seedOrder: UserId[],
): UserId[] {
  if (group.length === 1) return group

  const h2hWins: Record<UserId, number> = {}
  for (const id of group) h2hWins[id] = 0
  for (const m of matches) {
    // biome-ignore lint/style/noNonNullAssertion: player id is always set
    const s0 = m.sides[0].players[0].id!
    // biome-ignore lint/style/noNonNullAssertion: player id is always set
    const s1 = m.sides[1].players[0].id!
    if (!(s0 in h2hWins) || !(s1 in h2hWins)) continue
    if (m.sides[0].setsWon === m.sides[1].setsWon) continue
    const winner = m.sides[0].setsWon > m.sides[1].setsWon ? s0 : s1
    h2hWins[winner]++
  }

  const subGroups = new Map<number, UserId[]>()
  for (const id of group) {
    const w = h2hWins[id]
    const sg = subGroups.get(w) ?? []
    sg.push(id)
    subGroups.set(w, sg)
  }

  if (subGroups.size === 1) {
    // Fully cyclic — all have equal h2h wins, fall back to seeding
    return [...group].sort((a, b) => seedOrder.indexOf(a) - seedOrder.indexOf(b))
  }

  return [...subGroups.entries()]
    .sort(([wa], [wb]) => wb - wa)
    .flatMap(([, sg]) => rankTiedGroup(sg, matches, seedOrder))
}

/**
 * Derive the list of ranked player's userId from the results of round-robin matches.
 * Ranking criteria in order:
 *   1. Total sets won (descending)
 *   2. Total games won across all sets (descending)
 *   3. Within each tied group: h2h wins among only the tied players (recursively)
 *   4. Seeding position (seedOrder) — final stable tiebreak when h2h is fully cyclic
 */
export function getRanksForMatches(
  matches: EventMatch[],
  seedOrder: UserId[],
): UserId[] {
  const playerIds = uniq(
    matches.flatMap((match) => [
      // biome-ignore lint/style/noNonNullAssertion: player id is always set
      match.sides[0].players[0].id!,
      // biome-ignore lint/style/noNonNullAssertion: player id is always set
      match.sides[1].players[0].id!,
    ]),
  )

  const numberOfPlayers = playerIds.length
  if (matches.length !== (numberOfPlayers * (numberOfPlayers - 1)) / 2) {
    throw new Error(
      `Number of matches ${matches.length} does not satisfy round-robin requirement for ${numberOfPlayers} players`,
    )
  }

  const scoreMap = playerIds.reduce<
    Record<UserId, { setsWon: number; gamesWon: number }>
  >((acc, userId) => {
    acc[userId] = { setsWon: 0, gamesWon: 0 }
    return acc
  }, {})

  for (const match of matches) {
    // biome-ignore lint/style/noNonNullAssertion: player id is always set
    const side1PlayerId: UserId = match.sides[0].players[0].id!
    // biome-ignore lint/style/noNonNullAssertion: player id is always set
    const side2PlayerId: UserId = match.sides[1].players[0].id!

    for (const set of match.sets) {
      scoreMap[side1PlayerId].gamesWon += set.side1Score
      scoreMap[side2PlayerId].gamesWon += set.side2Score
      if (set.side1Score > set.side2Score) {
        scoreMap[side1PlayerId].setsWon += 1
      } else if (set.side2Score > set.side1Score) {
        scoreMap[side2PlayerId].setsWon += 1
      }
    }
  }

  // Group players by (setsWon, gamesWon), sort groups by descending score,
  // then rank within each tied group by h2h wins among only those players,
  // recursively. Fall back to seeding only when a sub-group is fully cyclic.
  const groups = new Map<string, UserId[]>()

  for (const id of playerIds) {
    const { setsWon, gamesWon } = scoreMap[id]
    const key = `${setsWon},${gamesWon}`
    const group = groups.get(key) ?? []
    group.push(id)
    groups.set(key, group)
  }

  const sortedGroups = [...groups.values()].sort((ga, gb) => {
    const a = scoreMap[ga[0]]
    const b = scoreMap[gb[0]]
    if (a.setsWon !== b.setsWon) return b.setsWon - a.setsWon
    return b.gamesWon - a.gamesWon
  })

  return sortedGroups.flatMap((g) => rankTiedGroup(g, matches, seedOrder))
}
